/**
 * sentry.ts
 *
 * Thin wrapper around @sentry/react.
 *
 * - `initObservability` must be called once per app before any route renders.
 * - All `beforeSend` and `beforeBreadcrumb` callbacks run `redact` so PII
 *   never reaches Sentry servers.
 * - `captureError` accepts safe context (ids, flags, release) but scrubs
 *   any residual PII in the context object before forwarding.
 * - Sentry releases are separate per app (platform vs backoffice).
 */

import * as Sentry from '@sentry/react';
import { configureLogger } from './logger.js';
import { redact } from './redact.js';

export type AppName = 'platform' | 'backoffice';

export type ObservabilityConfig = {
  /** Logical app name — used to namespace Sentry releases. */
  app: AppName;
  /** 'local' | 'preview' | 'staging' | 'production' */
  environment: string;
  /** git SHA or CI release identifier, e.g. "platform@abc1234". */
  release: string;
  /**
   * Raw release identifier (git SHA / CI build id, unprefixed by app name).
   * Surfaced via console + `window.__HOSTESA_RELEASE__` so support can ask
   * "what does the console say" without cross-referencing Sentry.
   */
  releaseId: string;
  /** Sentry DSN — must come from typed env config, never hardcoded. */
  dsn: string;
  /** Sampling rate 0–1.  Defaults to 1.0 in non-production. */
  tracesSampleRate?: number;
  /** Whether to enable Sentry session replay (off by default). */
  enableReplay?: boolean;
};

/** Shape of `window.__HOSTESA_RELEASE__`, set once at {@link initObservability}. */
export type HostesaReleaseInfo = {
  app: AppName;
  releaseId: string;
  environment: string;
};

declare global {
  interface Window {
    __HOSTESA_RELEASE__?: HostesaReleaseInfo;
  }
}

let _initialised = false;

/**
 * Initialise Sentry and the logger for the given app/environment.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initObservability(config: ObservabilityConfig): void {
  if (_initialised) return;
  _initialised = true;

  // Support-facing boot signal: lets anyone ask "what does the console say"
  // to identify exactly which build is running, without cross-referencing
  // Sentry. Deliberate console usage — sanctioned inside this package only.
  if (typeof window !== 'undefined') {
    window.__HOSTESA_RELEASE__ = {
      app: config.app,
      releaseId: config.releaseId,
      environment: config.environment,
    };
    // biome-ignore lint/suspicious/noConsole: deliberate, see comment above
    console.info(`[hostesa] ${config.app} release ${config.releaseId} (${config.environment})`);
  }

  configureLogger({
    environment: config.environment,
    sink: (entry) => {
      // Forward warn/error log entries as Sentry breadcrumbs.
      if (entry.level === 'warn' || entry.level === 'error') {
        Sentry.addBreadcrumb({
          category: 'logger',
          // Sentry's SeverityLevel uses 'warning', not 'warn'.
          level: entry.level === 'warn' ? 'warning' : 'error',
          message: entry.message,
          data:
            entry.context !== undefined
              ? (redact(entry.context) as Record<string, unknown>)
              : undefined,
          timestamp: Date.parse(entry.timestamp) / 1000,
        });
      }
    },
  });

  // Sentry.Integration typing varies across minor versions; use unknown[] for safety.
  const integrations: unknown[] = [Sentry.browserTracingIntegration()];

  if (config.enableReplay) {
    integrations.push(
      Sentry.replayIntegration({
        // Do not capture any replay media that might contain PII.
        maskAllText: true,
        blockAllMedia: true,
      }),
    );
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: `${config.app}@${config.release}`,
    integrations: integrations as NonNullable<Parameters<typeof Sentry.init>[0]>['integrations'],
    tracesSampleRate: config.tracesSampleRate ?? (config.environment === 'production' ? 0.1 : 1.0),
    replaysSessionSampleRate: config.enableReplay ? 0.0 : 0,
    replaysOnErrorSampleRate: config.enableReplay ? 1.0 : 0,

    beforeSend(event) {
      // Deep-scrub every field that might carry PII.
      if (event.extra) {
        event.extra = redact(event.extra) as typeof event.extra;
      }
      if (event.contexts) {
        event.contexts = redact(event.contexts) as typeof event.contexts;
      }
      if (event.user) {
        // Keep only non-PII user context.
        event.user = {
          id: event.user.id,
          username: event.user.username ? '[REDACTED]' : undefined,
          email: event.user.email ? '[REDACTED]' : undefined,
          ip_address: event.user.ip_address ? '[REDACTED]' : undefined,
        };
      }
      // Strip exception values of potential PII in the message.
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.value) {
            ex.value = ex.value
              .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[REDACTED]')
              .replace(/\+?[\d\s.\-()]{8,20}/g, '[REDACTED]');
          }
        }
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Scrub navigation breadcrumbs (URLs may contain guest tokens/emails).
      if (breadcrumb.category === 'navigation' && breadcrumb.data?.['to']) {
        breadcrumb.data['to'] = (breadcrumb.data['to'] as string).replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          '[REDACTED]',
        );
      }
      // Scrub fetch/XHR breadcrumbs — request bodies may contain PII.
      if ((breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') && breadcrumb.data) {
        if (breadcrumb.data['requestBody']) {
          breadcrumb.data['requestBody'] = '[REDACTED]';
        }
        if (breadcrumb.data['responseBody']) {
          breadcrumb.data['responseBody'] = '[REDACTED]';
        }
      }
      if (breadcrumb.message) {
        breadcrumb.message = breadcrumb.message.replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          '[REDACTED]',
        );
      }
      return breadcrumb;
    },
  });
}

/** Safe context attached to every error report — no PII fields allowed here. */
export type ErrorContext = {
  /** Resolved tenant UUID — safe to include. */
  tenantId?: string;
  /** Resolved hotel UUID — safe to include. */
  hotelId?: string;
  /** TanStack Router route ID. */
  routeId?: string;
  /** Active feature flags snapshot. */
  featureFlags?: Record<string, boolean>;
  /** Release identifier for the running app build. */
  releaseId?: string;
  /** Correlation ID returned by the backend on the failing request. */
  correlationId?: string;
};

/**
 * Capture an error with safe observability context.
 * The context object is scrubbed through `redact` as a safety net —
 * but callers MUST NOT pass PII-carrying fields like phone/email/transcript.
 */
export function captureError(error: unknown, context?: ErrorContext): void {
  const safeContext = context ? (redact(context) as Record<string, unknown>) : undefined;
  Sentry.captureException(error, {
    extra: safeContext,
  });
}

/**
 * Set the current Sentry user context using only non-PII identifiers.
 * Call after session is established. Pass `null` to clear on logout.
 */
export function setSentryUser(userId: string | null, role?: string): void {
  if (userId === null) {
    Sentry.setUser(null);
  } else {
    Sentry.setUser({ id: userId, role });
  }
}

/** Re-export Sentry's React error boundary for convenience. */
export { Sentry };
